/**
 * Generates the file path for NAR ingestion files.
 *
 * @param payerType - The payer/insurance type
 * @param subType - The plan/subtype
 * @param dayOfMonth - Day of the month
 * @param month - Month number (1-12)
 * @param year - Year number
 * @param documentType - File type (pdf or csv)
 * @param includeAuthGrid - Whether to include 'auth_grid/' prefix (default: true, set to false for file URLs)
 * @returns The file path in format: `auth_grid/{payerType}/{subType}_{dayOfMonth}_{month}_{year}.{documentType}`
 *          or `{payerType}/{subType}_{dayOfMonth}_{month}_{year}.{documentType}` if includeAuthGrid is false
 */
export const generateNarFilePath = (
  payerType: string,
  subType: string,
  dayOfMonth: number,
  month: number,
  year: number,
  documentType: string,
  includeAuthGrid: boolean = true,
): string => {
  const path = `${payerType}/${subType}_${dayOfMonth}_${month}_${year}.${documentType}`;
  return includeAuthGrid ? `auth_grid/${path}` : path;
};

/**
 * Generates the GCS file path for NAR ingestion files.
 *
 * @param payerType - The payer/insurance type
 * @param subType - The plan/subtype
 * @param dayOfMonth - Day of the month
 * @param month - Month number (1-12)
 * @param year - Year number
 * @param documentType - File type (pdf or csv)
 * @param bucketName - The GCS bucket name
 * @param includeAuthGrid - Whether to include 'auth_grid/' prefix (default: true)
 * @param includeGcsPrefix - Whether to include 'gs://{bucketName}/' prefix (default: true)
 * @returns The GCS file path in format: `gs://{bucketName}/auth_grid/{payerType}/{subType}_{dayOfMonth}_{month}_{year}.{documentType}`
 */
export const generateNarGcsFilePath = (
  payerType: string,
  subType: string,
  dayOfMonth: number,
  month: number,
  year: number,
  documentType: string,
  bucketName: string,
  includeAuthGrid: boolean = true,
  includeGcsPrefix: boolean = true,
): string => {
  const filePath = generateNarFilePath(
    payerType,
    subType,
    dayOfMonth,
    month,
    year,
    documentType,
    includeAuthGrid,
  );
  return includeGcsPrefix ? `gs://${bucketName}/${filePath}` : filePath;
};
